import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigateWithParams } from '../hooks/useNavigateWithParams';
import ReactMarkdown from 'react-markdown';
import { Play, Pause, Minus, Plus, ArrowUp, ChevronLeft, ChevronRight, List } from 'lucide-react';
import { extractFullScript, parseScriptSections } from '../lib/contentParsers';
import type { WebinarResults } from '../types/results';
import { STORAGE_KEYS } from '../config';

type FontSize = 'small' | 'medium' | 'large';

const FONT_CLASSES: Record<FontSize, string> = {
  small: 'prose-lg',
  medium: 'prose-xl',
  large: 'prose-2xl',
};

const SPEEDS = [
  { label: 'Extra Slow', pxPerFrame: 0.25 },
  { label: 'Slow', pxPerFrame: 0.5 },
  { label: 'Medium', pxPerFrame: 1 },
  { label: 'Fast', pxPerFrame: 2 },
];

export function Presenter() {
  const navigate = useNavigateWithParams();
  const [script, setScript] = useState('');
  const [fontSize, setFontSize] = useState<FontSize>('medium');
  const [isScrolling, setIsScrolling] = useState(false);
  const [speedIndex, setSpeedIndex] = useState(2); // Medium
  const [scrollProgress, setScrollProgress] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  const contentRef = useRef<HTMLDivElement>(null);
  const animFrameRef = useRef<number | null>(null);
  const controlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());

  // Parse section titles for navigation
  const [sectionTitles, setSectionTitles] = useState<string[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.webinarResults);
    if (!stored) {
      navigate('/results', { replace: true });
      return;
    }

    try {
      const results = JSON.parse(stored) as WebinarResults;
      const fullScript = extractFullScript(results.webinarScript || '');
      setScript(fullScript);

      // Extract section titles
      const sections = parseScriptSections(results.webinarScript || '');
      setSectionTitles(sections.map((s) => s.title));
    } catch {
      navigate('/results', { replace: true });
    }
  }, [navigate]);

  // Detect current section via IntersectionObserver
  useEffect(() => {
    if (!contentRef.current || sectionTitles.length === 0) return;

    // Find all heading elements in the content
    const headings = contentRef.current.querySelectorAll('h1, h2, h3');
    sectionRefs.current.clear();
    headings.forEach((heading) => {
      const text = heading.textContent?.trim() || '';
      sectionRefs.current.set(text, heading as HTMLElement);
    });

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const text = entry.target.textContent?.trim() || '';
            const idx = sectionTitles.findIndex(
              (t) => text.toLowerCase().includes(t.toLowerCase()) || t.toLowerCase().includes(text.toLowerCase())
            );
            if (idx !== -1) {
              setCurrentSectionIndex(idx);
            }
          }
        }
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0.1 }
    );

    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [script, sectionTitles]);

  // Auto-hide controls after 3s of inactivity
  const resetControlsTimer = useCallback(() => {
    setControlsVisible(true);
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    controlsTimerRef.current = setTimeout(() => {
      if (isScrolling) {
        setControlsVisible(false);
        setSidebarOpen(false);
      }
    }, 3000);
  }, [isScrolling]);

  useEffect(() => {
    const handleMouseMove = () => resetControlsTimer();
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [resetControlsTimer]);

  // Track scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-scroll animation loop
  useEffect(() => {
    if (!isScrolling) {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      return;
    }

    const speed = SPEEDS[speedIndex].pxPerFrame;

    function tick() {
      window.scrollBy(0, speed);

      const atBottom =
        window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2;
      if (atBottom) {
        setIsScrolling(false);
        return;
      }

      animFrameRef.current = requestAnimationFrame(tick);
    }

    animFrameRef.current = requestAnimationFrame(tick);
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isScrolling, speedIndex]);

  // Navigate to section by index
  const scrollToSection = useCallback(
    (index: number) => {
      if (index < 0 || index >= sectionTitles.length) return;
      const title = sectionTitles[index];

      // Find heading in DOM
      if (!contentRef.current) return;
      const headings = contentRef.current.querySelectorAll('h1, h2, h3');
      for (const heading of headings) {
        const text = heading.textContent?.trim() || '';
        if (
          text.toLowerCase().includes(title.toLowerCase()) ||
          title.toLowerCase().includes(text.toLowerCase())
        ) {
          setIsScrolling(false);
          heading.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setCurrentSectionIndex(index);
          return;
        }
      }
    },
    [sectionTitles]
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (sidebarOpen) {
          setSidebarOpen(false);
        } else {
          navigate('/results');
        }
      } else if (e.key === ' ') {
        e.preventDefault();
        setIsScrolling((prev) => !prev);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSpeedIndex((prev) => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSpeedIndex((prev) => Math.min(SPEEDS.length - 1, prev + 1));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollToSection(currentSectionIndex - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollToSection(currentSectionIndex + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, currentSectionIndex, scrollToSection, sidebarOpen]);

  const cycleFontSize = (direction: 'up' | 'down') => {
    const sizes: FontSize[] = ['small', 'medium', 'large'];
    const idx = sizes.indexOf(fontSize);
    if (direction === 'up' && idx < sizes.length - 1) setFontSize(sizes[idx + 1]);
    if (direction === 'down' && idx > 0) setFontSize(sizes[idx - 1]);
  };

  const scrollToTop = () => {
    setIsScrolling(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentSectionName = sectionTitles[currentSectionIndex] || '';

  if (!script) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-white/10">
        <div
          className="h-full bg-primary-500 transition-[width] duration-100"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Section sidebar */}
      {sectionTitles.length > 0 && (
        <div
          className={`fixed left-0 top-0 bottom-0 z-40 w-72 bg-gray-900/95 backdrop-blur-md border-r border-gray-700 transform transition-transform duration-300 ${
            sidebarOpen && controlsVisible ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="p-4 pt-8 overflow-y-auto h-full">
            <h3 className="text-xs font-bold uppercase tracking-wider text-white/40 mb-4">
              Sections
            </h3>
            <div className="space-y-1">
              {sectionTitles.map((title, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    scrollToSection(idx);
                    setSidebarOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors truncate ${
                    idx === currentSectionIndex
                      ? 'bg-primary-500/20 text-primary-300 border border-primary-500/30'
                      : 'text-white/60 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <span className="text-white/30 mr-2 text-xs">{idx + 1}.</span>
                  {title}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Controls overlay */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 transition-opacity duration-300 ${
          controlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Current section indicator */}
        {currentSectionName && (
          <div className="text-center mb-2">
            <span className="inline-block px-4 py-1.5 rounded-full bg-gray-900/90 backdrop-blur-md border border-gray-700 text-xs text-white/60">
              {currentSectionIndex + 1}/{sectionTitles.length}: {currentSectionName}
            </span>
          </div>
        )}

        <div className="bg-gray-900/95 backdrop-blur-md border-t border-gray-600 py-4 px-6">
          <div className="max-w-2xl mx-auto flex items-center justify-between gap-4">
            {/* Sections + Font size */}
            <div className="flex items-center gap-2">
              {sectionTitles.length > 0 && (
                <button
                  onClick={() => setSidebarOpen((prev) => !prev)}
                  className={`p-2 rounded-lg transition-colors ${
                    sidebarOpen
                      ? 'bg-primary-500/20 text-primary-400'
                      : 'bg-white/10 hover:bg-white/20 text-white/70 hover:text-white'
                  }`}
                  title="Toggle section list"
                >
                  <List className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => cycleFontSize('down')}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white/70 hover:text-white"
                title="Decrease font size"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="text-xs text-white/50 w-12 text-center">
                {fontSize.charAt(0).toUpperCase() + fontSize.slice(1)}
              </span>
              <button
                onClick={() => cycleFontSize('up')}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white/70 hover:text-white"
                title="Increase font size"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {/* Section nav + Play/Pause + Speed */}
            <div className="flex items-center gap-3">
              {/* Prev section */}
              <button
                onClick={() => scrollToSection(currentSectionIndex - 1)}
                disabled={currentSectionIndex <= 0}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white/70 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                title="Previous section (←)"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <button
                onClick={() => setIsScrolling((prev) => !prev)}
                className="p-3 rounded-full bg-primary-500/20 hover:bg-primary-500/30 border border-primary-500/30 transition-colors"
                title={isScrolling ? 'Pause (SPACE)' : 'Play (SPACE)'}
              >
                {isScrolling ? (
                  <Pause className="w-5 h-5 text-primary-400" />
                ) : (
                  <Play className="w-5 h-5 text-primary-400" />
                )}
              </button>

              {/* Next section */}
              <button
                onClick={() => scrollToSection(currentSectionIndex + 1)}
                disabled={currentSectionIndex >= sectionTitles.length - 1}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white/70 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                title="Next section (→)"
              >
                <ChevronRight className="w-4 h-4" />
              </button>

              <div className="flex items-center gap-1">
                {SPEEDS.map((speed, idx) => (
                  <button
                    key={speed.label}
                    onClick={() => setSpeedIndex(idx)}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      speedIndex === idx
                        ? 'bg-primary-500/30 text-primary-300 border border-primary-500/30'
                        : 'text-white/40 hover:text-white/70 hover:bg-white/10'
                    }`}
                  >
                    {speed.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Scroll to top + Exit */}
            <div className="flex items-center gap-2">
              <button
                onClick={scrollToTop}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white/70 hover:text-white"
                title="Scroll to top"
              >
                <ArrowUp className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/results')}
                className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs text-white/50 hover:text-white transition-colors"
              >
                ESC Exit
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Script content - narrow teleprompter column */}
      <div className="max-w-xl mx-auto px-6 py-16 pb-32" ref={contentRef}>
        <div
          className={`prose prose-invert ${FONT_CLASSES[fontSize]} max-w-none
            prose-headings:text-white prose-headings:text-center prose-headings:mb-6 prose-headings:mt-12
            prose-p:text-gray-100 prose-p:text-center prose-p:leading-[2] prose-p:mb-6
            prose-strong:text-white prose-strong:font-bold
            prose-li:text-gray-200 prose-li:text-center
            prose-hr:border-gray-700 prose-hr:my-8`}
          style={{ letterSpacing: '0.01em' }}
        >
          <ReactMarkdown>{script}</ReactMarkdown>
        </div>
      </div>

      {/* Keyboard hints */}
      <div className="fixed top-4 right-4 z-50 px-3 py-1.5 rounded-lg bg-gray-900/90 border border-gray-700 text-white/60 text-xs backdrop-blur-md space-y-0.5">
        <div>SPACE Play/Pause</div>
        <div>← → Sections</div>
        <div>↑ ↓ Speed</div>
        <div>ESC Exit</div>
      </div>
    </div>
  );
}
